import { MouseEvent, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import { NodeProps, Position } from 'reactflow';
import { CHILD_NODE_PADDING, GRAPH_CHILD_NODE_HEIGHT } from './constants';
import { ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import { CompactPanelRow } from './GraphNodeCompactParts';
import GraphNodeHandle from './GraphNodeHandle';
import NodeContextMenu from './NodeContextMenu';
import useCompactPanelBorderColor from './hooks/useCompactPanelBorderColor';
import {
  getFilterHighlightBackground,
  isFilterMatch,
} from './utils/lineageDisplaySettings';

const GraphChildNodeCompact = (node: NodeProps<ReactFlowNodeData>) => {
  const { id, data } = node;
  const theme = useTheme();
  const { highlightedNode, isControlled } = useLineageGraphContext();
  // Columns of a nested element (e.g. a Sigma element inside a page) are
  // indented under it and laid out at the element's width.
  const isNested = !!data.parentNode?.parent;
  const borderColor = useCompactPanelBorderColor(
    data.parentNode?.parent ?? data.parentNode?.id,
  );

  const rowRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const handleMenuOpen = (e: MouseEvent) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const isHighlighted = highlightedNode?.id === id;
  const isOnHighlightPath = isHighlighted || !!data.isOnHighlightPath;

  return (
    <CompactPanelRow
      rowRef={rowRef}
      width={data.width + (isNested ? CHILD_NODE_PADDING * 2 : 0)}
      borderColor={borderColor}
      label={data.metadata.label ?? ''}
      isLast={!!data.isLastCompactRow}
      isIndented={isNested}
      isHighlighted={isHighlighted}
      isOnHighlightPath={isOnHighlightPath}
      filterBackground={
        isFilterMatch(data)
          ? getFilterHighlightBackground(theme.palette)
          : undefined
      }
      onContextMenu={handleMenuOpen}
    >
      <GraphNodeHandle
        type="target"
        position={Position.Left}
        $isHighlighted={isOnHighlightPath}
        $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
      />
      <GraphNodeHandle
        type="source"
        position={Position.Right}
        $isHighlighted={isOnHighlightPath}
        $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
      />

      {!isControlled && (
        <NodeContextMenu
          hideMenuButton
          showMenuButton={false}
          node={node}
          pos={menuPos}
          onClose={() => setMenuPos(null)}
          onHighlightNodeClick={() => rowRef.current?.click()}
        />
      )}
    </CompactPanelRow>
  );
};

export default GraphChildNodeCompact;
